import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../contexts/CityContext';
import { useAuth } from '../contexts/AuthContext';
import { useDeliveries } from '../hooks/useDeliveries';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';
import { BottomNav } from '../components/BottomNav';
import api from '../services/api';

const TABS = [
  { id: 'disponiveis', label: 'Disponíveis' },
  { id: 'meus', label: 'Meus pedidos' },
  { id: 'fretes', label: 'Fretes' },
];

const VEHICLE_EMOJI = { moto: '🛵', bike: '🚲', car: '🚗', van: '🚐' };
const VEHICLE_LABEL = { moto: 'Moto', bike: 'Bicicleta', car: 'Carro', van: 'Van' };

export function DeliveriesPage() {
  const { selectedCity } = useCity();
  const { isVisitor } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('disponiveis');
  const { deliverers, loading, error, refetch } = useDeliveries(selectedCity?.id);

  // Formulário de solicitação (LE-014)
  const [selectedDeliverer, setSelectedDeliverer] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  // LE-020 (S4-a): avaliações dos entregadores (GET /deliveries/:id/reviews)
  const [expandedReviews, setExpandedReviews] = useState(null); // delivery profile id
  const [reviewsCache, setReviewsCache] = useState({});
  const [reviewsLoadingId, setReviewsLoadingId] = useState(null);

  const toggleReviews = async (d) => {
    if (expandedReviews === d.id) {
      setExpandedReviews(null);
      return;
    }
    setExpandedReviews(d.id);
    if (!reviewsCache[d.id]) {
      setReviewsLoadingId(d.id);
      try {
        const { data } = await api.get(`/deliveries/${d.id}/reviews`);
        setReviewsCache(prev => ({ ...prev, [d.id]: data.data || [] }));
      } catch {
        setReviewsCache(prev => ({ ...prev, [d.id]: [] }));
      } finally {
        setReviewsLoadingId(null);
      }
    }
  };

  // Limpa os campos do formulário sem apagar a mensagem de sucesso (que é
  // exibida logo após o envio antes do redirect).
  const resetForm = () => {
    setSelectedDeliverer(null);
    setPickupAddress('');
    setDeliveryAddress('');
    setNotes('');
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDeliverer) {
      setFormError('Escolha um entregador para solicitar a entrega.');
      return;
    }
    if (!deliveryAddress.trim()) {
      setFormError('Informe o endereço de entrega.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      await api.post('/orders', {
        delivery_id: selectedDeliverer.id,
        pickup_address: pickupAddress.trim() || undefined,
        delivery_address: deliveryAddress.trim(),
        notes: notes.trim() || undefined,
      });
      resetForm();
      setFormSuccess('Pedido de entrega solicitado! Acompanhe em "Meus pedidos".');
      setTimeout(() => navigate('/pedidos'), 800);
    } catch (err) {
      setFormError(err.response?.data?.error?.message || 'Erro ao solicitar entrega. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 10, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 5 };

  return (
    <div className="nc-page nc-page--nav">
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--nc-border)', padding: '12px 16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--nc-text)', margin: 0 }}>Entregas</h1>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </div>
        <div className="nc-pills" role="group" aria-label="Abas de entregas">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'nc-pill nc-pill--active' : 'nc-pill'}
              aria-pressed={activeTab === tab.id}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {activeTab === 'disponiveis' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Entregadores próximos
            </div>
            {loading ? <Loading /> : error ? (
              <div role="alert" style={{ padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 13 }}>
                {error}
                <button onClick={refetch} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
              </div>
            ) : deliverers.length === 0 ? (
              <EmptyState
                icon="🛵"
                title="Nenhum entregador disponível"
                description="No momento não há entregadores ativos nesta cidade. Volte mais tarde!"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {deliverers.map(d => (
                  <div
                    key={d.id}
                    style={{
                      background: 'var(--nc-surface)', borderRadius: 10, overflow: 'hidden',
                      border: selectedDeliverer?.id === d.id ? '1.5px solid #0d5c3b' : '1px solid var(--nc-border)'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => { setSelectedDeliverer(d); setFormError(null); }}
                      aria-pressed={selectedDeliverer?.id === d.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                        width: '100%', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%', background: 'var(--nc-surface-alt)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                      }}>{VEHICLE_EMOJI[d.vehicle_type] || '🛵'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{d.name}</div>
                        <div className="nc-hint">
                          {VEHICLE_LABEL[d.vehicle_type] || 'Entregador'}
                          {d.avg_rating ? (
                            <>
                              {' '}• <span style={{ color: 'var(--nc-warning)' }}>{'★'.repeat(Math.max(0, Math.min(5, Math.round(d.avg_rating))))}</span> {Number(d.avg_rating).toFixed(1)}{' '}
                              ({d.review_count ?? 0})
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-primary)' }}>
                        R$ {Number(d.base_price ?? 0).toFixed(2).replace('.', ',')}
                      </div>
                    </button>

                    {/* LE-020 (S4-a): expandir avaliações do entregador */}
                    <button
                      type="button"
                      onClick={() => toggleReviews(d)}
                      aria-expanded={expandedReviews === d.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                        padding: '8px 12px', cursor: 'pointer',
                        border: 'none', borderTop: '1px solid var(--nc-border)',
                        background: expandedReviews === d.id ? '#f0fdf4' : '#fafaf9'
                      }}
                    >
                      <span style={{ fontSize: 12 }}>💬</span>
                      <span className="nc-hint">
                        Ver avaliações ({d.review_count ?? 0})
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--nc-text-muted)' }}>
                        {expandedReviews === d.id ? '▲' : '▼'}
                      </span>
                    </button>

                    {expandedReviews === d.id && (
                      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--nc-border)' }}>
                        {reviewsLoadingId === d.id ? (
                          <Loading />
                        ) : (reviewsCache[d.id] || []).length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--nc-text-muted)' }}>Ainda sem avaliações.</div>
                        ) : (
                          (reviewsCache[d.id] || []).map(r => (
                            <div key={r.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--nc-surface-alt)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nc-text)' }}>{r.user_name || 'Usuário'}</span>
                                <span style={{ fontSize: 11, color: 'var(--nc-warning)' }}>
                                  {'★'.repeat(Math.max(0, Math.min(5, Math.round(r.rating))))} {Number(r.rating).toFixed(1)}
                                </span>
                              </div>
                              {r.comment && (
                                <div style={{ fontSize: 12, color: 'var(--nc-text-dim)', marginTop: 2 }}>{r.comment}</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Formulário de solicitação (LE-014) */}
            <div style={{
              background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)',
              padding: 14, marginTop: 16
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 4 }}>Solicitar entrega</div>

              {isVisitor ? (
                <>
                  <p className="nc-hint" style={{ marginBottom: 12 }}>
                    Faça login para solicitar uma entrega e acompanhar o status do pedido.
                  </p>
                  <button className="nc-btn-primary" onClick={() => navigate('/entrar')} style={{ width: '100%' }}>
                    Entrar / Criar conta
                  </button>
                </>
              ) : (
                <form onSubmit={handleSubmit}>
                  {selectedDeliverer ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      background: 'var(--nc-success-soft)', borderRadius: 10, border: '1px solid var(--nc-success-border)', marginBottom: 10
                    }}>
                      <span style={{ fontSize: 18 }}>{VEHICLE_EMOJI[selectedDeliverer.vehicle_type] || '🛵'}</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#166534' }}>
                        {selectedDeliverer.name}
                      </span>
                      <button type="button" onClick={resetForm} style={{
                        background: 'none', border: 'none', color: 'var(--nc-primary)', fontSize: 12, cursor: 'pointer'
                      }}>Trocar</button>
                    </div>
                  ) : (
                    <p style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 10 }}>
                      Escolha um entregador acima para continuar.
                    </p>
                  )}

                  <label style={labelStyle}>Endereço de entrega *</label>
                  <input
                    type="text"
                    value={deliveryAddress}
                    onChange={e => setDeliveryAddress(e.target.value)}
                    placeholder="Rua, número, bairro"
                    required
                    aria-label="Endereço de entrega"
                    style={inputStyle}
                  />

                  <label style={labelStyle}>Endereço de retirada (opcional)</label>
                  <input
                    type="text"
                    value={pickupAddress}
                    onChange={e => setPickupAddress(e.target.value)}
                    placeholder="Onde o entregador busca o item"
                    aria-label="Endereço de retirada"
                    style={inputStyle}
                  />

                  <label style={labelStyle}>Observações (opcional)</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Ex.: pacote frágil, chamar no portão"
                    aria-label="Observações"
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />

                  {formError && (
                    <div role="alert" style={{
                      padding: '9px 12px', borderRadius: 8, background: 'var(--nc-danger-soft)',
                      border: '1px solid var(--nc-danger-border)', color: 'var(--nc-danger-text)', fontSize: 12, marginBottom: 10
                    }}>
                      {formError}
                    </div>
                  )}
                  {formSuccess && (
                    <div role="status" style={{
                      padding: '9px 12px', borderRadius: 8, background: 'var(--nc-success-soft)',
                      border: '1px solid var(--nc-success-border)', color: 'var(--nc-success-text)', fontSize: 12, marginBottom: 10
                    }}>
                      {formSuccess}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !selectedDeliverer}
                    className="nc-btn-primary"
                    style={{ width: '100%', opacity: submitting || !selectedDeliverer ? 0.6 : 1 }}
                  >
                    {submitting ? 'Solicitando...' : 'Solicitar entrega'}
                  </button>
                </form>
              )}
            </div>
          </>
        )}

        {activeTab === 'meus' && (
          <EmptyState
            icon="📦"
            title="Acompanhe seus pedidos"
            description="Os pedidos de entrega que você solicitou aparecem em Meus pedidos, com status atualizado."
            action={
              <button className="nc-btn-primary" onClick={() => navigate('/pedidos')}>Ver meus pedidos</button>
            }
          />
        )}

        {activeTab === 'fretes' && (
          <EmptyState
            icon="🚚"
            title="Em breve"
            description="O serviço de fretes será liberado em breve."
          />
        )}
      </div>

      <BottomNav />
    </div>
  );
}
